import React from "react";
import PropTypes from "prop-types";
import { animated, config, useSpring, useTransition } from "react-spring";

import "./TextAnimation.scss";

export const TextAnimationType = {
  HORIZONTAL_LEFT_TO_RIGHT: "HLR",
  HORIZONTAL_RIGHT_TO_LEFT: "HRL",
  VERTICAL_TOP_TO_BOTTOM: "VTB",
  VERTICAL_BOTTOM_TO_TOP: "VBT"
};
const newText = text => ({ key: `${Date.now()}`, data: text });

const TextAnimation = ({ text, delay, className, style, noOverflow, configSpring, direction, position }) => {
  const placeholderRef = React.useRef(null);
  const [content, setContent] = React.useState(() => newText(text));
  const [timeoutId, setTimeoutId] = React.useState(0);
  const [isFirstRun, setIsFirstRun] = React.useState(true);
  const getTranslation = val => {
    let translation;
    switch (direction) {
      case TextAnimationType.HORIZONTAL_LEFT_TO_RIGHT:
        translation = `translateX(${-val}%)`;
        break;
      case TextAnimationType.HORIZONTAL_RIGHT_TO_LEFT:
        translation = `translateX(${val}%)`;
        break;
      case TextAnimationType.VERTICAL_TOP_TO_BOTTOM:
        translation = `translateY(${-val}%)`;
        break;
      default:
        translation = `translateY(${val}%)`;
    }
    return translation;
  };
  const [width] = React.useState({ width: "auto" });
  const transitions = useTransition(content, item => item.key, {
    from: { opacity: 0, transform: getTranslation(30) },
    enter: { opacity: 1, transform: getTranslation(0) },
    leave: { opacity: 0, transform: getTranslation(-15) },
    config: configSpring,
    immediate: isFirstRun,
    onDestroyed: () => setIsFirstRun(false)
  });
  const { width: animatedWidth } = useSpring({
    to: width,
    config: configSpring,
    immediate: isFirstRun
  });
  React.useEffect(() => {
    setTimeoutId(
      setTimeout(() => {
        if (!placeholderRef.current) return;
        placeholderRef.current.innerHTML = text;
        setContent(newText(text));
      }, delay)
    );
  }, [text]);

  React.useEffect(() => () => clearTimeout(timeoutId), []);

  return (
    <animated.div className={`text-animation ${className}`} style={{ ...style, width: animatedWidth }}>
      <span ref={placeholderRef} className="text-animation--placeholder" />
      <div className="text-animation--inner" style={{ overflow: noOverflow ? "hidden" : "visible" }}>
        {transitions.map(({ item, props, key }) => (
          <animated.div key={key} style={{ ...props, position }}>
            {item.data}
          </animated.div>
        ))}
      </div>
    </animated.div>
  );
};

TextAnimation.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  direction: PropTypes.string,
  inline: PropTypes.bool,
  noOverflow: PropTypes.bool,
  delay: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
  configSpring: PropTypes.any
};

TextAnimation.defaultProps = {
  direction: TextAnimationType.VERTICAL_BOTTOM_TO_TOP,
  noOverflow: true,
  configSpring: config.default,
  delay: 0,
  className: "",
  style: {},
  position: "absolute"
};

export default TextAnimation;
